import React, { useState } from 'react';
import { X, Briefcase, ArrowLeft, CheckCircle } from 'lucide-react';

const ConversionFlow = ({ lead, onConvert, onClose, saving }) => {
  // Pre-fill from lead data
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Default to tomorrow at 10:00
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [surveyDate, setSurveyDate] = useState(tomorrow.toISOString().split('T')[0]);
  const [surveyTime, setSurveyTime] = useState('10:00');
  const [duration, setDuration] = useState(60);

  const [success, setSuccess] = useState(null);

  if (!lead) return null;

  const address = [lead.address_line_1, lead.address_line_2, lead.address_town, lead.address_postcode]
    .filter(Boolean).join(', ');

  const handleConvert = async () => {
    try {
      const result = await onConvert(lead.id, {
        contact_name: contactName.trim() || null,
        email: email.trim() || null,
        survey_date: surveyDate,
        survey_time: surveyTime,
        survey_duration: duration,
        notes: notes.trim() || null
      });
      setSuccess(result);
    } catch (err) {
      // Error handled by parent
    }
  };

  // Success state
  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="bg-white w-full max-w-md rounded-2xl p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Survey Booked!</h2>
          <p className="text-gray-600 mb-1">{lead.business_name}</p>
          <p className="text-sm text-gray-500 mb-6">
            {surveyDate} at {surveyTime}
          </p>
          <button
            onClick={onClose}
            className="w-full min-h-[52px] rounded-xl bg-green-600 text-white font-bold text-base active:bg-green-800 transition-all"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Time shortcuts
  const timeSlots = [
    { label: '9am', value: '09:00' },
    { label: '10am', value: '10:00' },
    { label: '11am', value: '11:00' },
    { label: '12pm', value: '12:00' },
    { label: '1pm', value: '13:00' },
    { label: '2pm', value: '14:00' },
    { label: '3pm', value: '15:00' },
    { label: '4pm', value: '16:00' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <h3 className="text-lg font-bold text-gray-900">Book Survey</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Business info (read-only, from lead) */}
          <div className="bg-blue-50 rounded-xl p-3">
            <div className="font-bold text-blue-900">{lead.business_name}</div>
            {lead.phone_number && (
              <div className="text-sm text-blue-700 mt-1">{lead.phone_number}</div>
            )}
            {address && (
              <div className="text-sm text-blue-600 mt-1">{address}</div>
            )}
            {lead.category_name && (
              <div className="text-xs text-blue-500 mt-1">{lead.category_name}</div>
            )}
          </div>

          {/* Contact name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name (optional)</label>
            <input
              type="text"
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              placeholder="Person you spoke to"
              className="w-full min-h-[48px] rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="business@example.com"
              className="w-full min-h-[48px] rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4"
            />
          </div>

          {/* Survey date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Survey Date *</label>
            <input
              type="date"
              value={surveyDate}
              onChange={e => setSurveyDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full min-h-[48px] rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4"
            />
          </div>

          {/* Survey time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Survey Time *</label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {timeSlots.map(t => (
                <button
                  key={t.value}
                  onClick={() => setSurveyTime(t.value)}
                  className={`min-h-[40px] rounded-lg text-sm font-medium transition-all
                    ${surveyTime === t.value
                      ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500'
                      : 'bg-gray-50 text-gray-700 active:bg-gray-100'
                    }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <input
              type="time"
              value={surveyTime}
              onChange={e => setSurveyTime(e.target.value)}
              className="w-full min-h-[48px] rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <div className="grid grid-cols-3 gap-2">
              {[30, 60, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setDuration(d)}
                  className={`min-h-[44px] rounded-xl text-sm font-medium transition-all
                    ${duration === d
                      ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500'
                      : 'bg-gray-50 text-gray-700 active:bg-gray-100'
                    }`}
                >
                  {d} min
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any notes about the survey..."
              rows={2}
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base p-3"
            />
          </div>

          {/* Convert button */}
          <button
            onClick={handleConvert}
            disabled={saving || !surveyDate || !surveyTime}
            className="w-full flex items-center justify-center gap-2 min-h-[56px] rounded-xl bg-blue-600 active:bg-blue-800 text-white font-bold text-lg disabled:opacity-50 transition-all"
          >
            <Briefcase size={22} />
            <span>{saving ? 'Booking...' : 'Book Survey'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversionFlow;
